/* eslint-disable valid-typeof */
import React, { useState, useCallback, useEffect } from "react";
import { loadWeb3, getWeb3Instance } from "./Connectivity/Connectivity";
import { toast } from "react-toastify";
import {
  pynexnftMarketplace,
  pynexnftMarketplaceAbi,
  USDT,
  USDTAbi,
} from "./Contract";
import "bootstrap/dist/css/bootstrap.min.css";

export default function MarketplacePage() {
  const [btnTxt, setBtnTxt] = useState("Connect Wallet");
  const [account, setAccount] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [loadingStates, setLoadingStates] = useState({});

  // Function inputs and outputs storage
  const [functionInputs, setFunctionInputs] = useState({});
  const [functionOutputs, setFunctionOutputs] = useState({});

  // Overview data
  const [marketplaceData, setMarketplaceData] = useState({
    totalNftsMinted: "",
    maxNftSupply: "",
    nftBasePrice: "",
    queueLength: "",
    isClaimEnabled: false,
    usdtBalance: "",
    userUsdtBalance: "",
  });

  // ─────────────────────────────────────────────────────────────
  //  FUNCTION DEFINITIONS — fully synced with ABI
  // ─────────────────────────────────────────────────────────────
  const marketplaceFunctions = {
    // ── READ FUNCTIONS ──────────────────────────────────────────
    readFunctions: [
      // Basic Info
      { name: "adminWallet", title: "Admin Wallet", description: "Get admin wallet address", inputs: [], icon: "👑", category: "Basic Info" },
      { name: "burnFeeReceiver", title: "Burn Fee Receiver", inputs: [], icon: "🔥", category: "Basic Info" },
      { name: "platformRewardReceiver", title: "Platform Reward Receiver", inputs: [], icon: "🏢", category: "Basic Info" },
      { name: "nftContract", title: "NFT Contract", inputs: [], icon: "🎨", category: "Basic Info" },
      { name: "nftMatrixContract", title: "NFT Matrix Contract", inputs: [], icon: "🤖", category: "Basic Info" },
      { name: "nftReward", title: "NFT Reward Contract", inputs: [], icon: "🏆", category: "Basic Info" },
      { name: "owner", title: "Contract Owner", inputs: [], icon: "👑", category: "Basic Info" },
      { name: "paused", title: "Contract Paused", inputs: [], icon: "⏸️", category: "Basic Info", formatOutput: (v) => v ? "Yes" : "No" },
      { name: "usdtToken", title: "USDT Token", inputs: [], icon: "💰", category: "Basic Info" },

      // Config & Stats
      { name: "buyCount", title: "Total Buy Count", inputs: [], icon: "🛒", category: "Stats" },
      { name: "totalNftsMinted", title: "Total NFTs Minted", inputs: [], icon: "🎨", category: "Stats" },
      { name: "mintNftCount", title: "Mint NFT Count", inputs: [], icon: "🔢", category: "NFT Config" },
      { name: "maxNftSupply", title: "Max NFT Supply", inputs: [], icon: "📈", category: "NFT Config" },
      { name: "maxSalePrice", title: "Max Sale Price", inputs: [], icon: "💲", category: "Trading" },
      { name: "nftBasePrice", title: "NFT Base Price", inputs: [], icon: "💵", category: "NFT Config" },
      { name: "nftBurnBasePrice", title: "NFT Burn Base Price", inputs: [], icon: "🔥", category: "NFT Config" },
      { name: "globalPeriod", title: "Global Period", inputs: [], icon: "⏱️", category: "Stats" },
      { name: "queueRequire", title: "Queue Require", inputs: [], icon: "📋", category: "Queue Management" },
      { name: "sellPercent", title: "Sell Percent", inputs: [], icon: "📉", category: "Trading", formatOutput: (v) => `${v}%` },

      // Income Percentages
      { name: "buringIncomePercent", title: "Burning Income %", inputs: [], icon: "🔥", category: "Income", formatOutput: (v) => `${v}%` },
      { name: "levelIncomePercent", title: "Level Income %", inputs: [], icon: "📊", category: "Income", formatOutput: (v) => `${v}%` },
      { name: "nftOwnerIncomePercent", title: "NFT Owner Income %", inputs: [], icon: "💰", category: "Income", formatOutput: (v) => `${v}%` },
      { name: "platformIncomePercent", title: "Platform Income %", inputs: [], icon: "🏢", category: "Income", formatOutput: (v) => `${v}%` },

      // Status
      { name: "isClaimEnable", title: "Claim Status", inputs: [], icon: "✅", category: "Basic Info", formatOutput: (v) => v ? "Enabled" : "Disabled" },
      { name: "isAutoTradeFee", title: "Auto Trade Fee Enabled", inputs: [], icon: "💸", category: "Auto Trade", formatOutput: (v) => v ? "Yes" : "No" },

      // Auto Trade
      { name: "autoTradeFee", title: "Auto Trade Fee", inputs: [], icon: "💸", category: "Auto Trade" },
      { name: "autoTradeFeeReceiver", title: "Auto Trade Fee Receiver", inputs: [], icon: "📥", category: "Auto Trade" },
      { name: "botCount", title: "Total Bot Count", inputs: [], icon: "🤖", category: "Auto Trade" },
      {
        name: "botInfo",
        title: "Bot Info",
        inputs: [{ name: "", type: "address", placeholder: "Enter bot address", required: true, label: "Bot Address" }],
        icon: "🤖",
        category: "Auto Trade",
        formatOutput: (v) => `Last Timestamp: ${v[0]}\nBot ID: ${v[1]}\nEnabled: ${v[2] ? "Yes" : "No"}`,
      },
      { name: "botById", title: "Bot By ID", inputs: [{ name: "", type: "uint256", placeholder: "Enter Bot Index", required: true, label: "Bot Index" }], icon: "🤖", category: "Auto Trade" },
      { name: "getBotAddress", title: "Get Bot Address", inputs: [{ name: "botId", type: "uint256", placeholder: "Enter Bot ID", required: true, label: "Bot ID" }], icon: "🤖", category: "Auto Trade" },
      { name: "getBotId", title: "Get Bot ID", inputs: [{ name: "wallet", type: "address", placeholder: "Enter Wallet Address", required: true, label: "Wallet" }], icon: "🤖", category: "Auto Trade" },
      { name: "getEligibleTradeBots", title: "Eligible Trade Bots", inputs: [], icon: "🤖", category: "Auto Trade", formatOutput: (v) => (v.length ? v.join(", ") : "None") },

      // Queue Management
      { name: "nftQueueLength", title: "NFT Queue Length", inputs: [], icon: "📏", category: "Queue Management" },
      { name: "nftQueueLiveLength", title: "NFT Queue Live Length", inputs: [], icon: "📡", category: "Queue Management" },
      { name: "nftQueueStart", title: "NFT Queue Start", inputs: [], icon: "⬆️", category: "Queue Management" },
      { name: "nftQueueEnd", title: "NFT Queue End", inputs: [], icon: "⬇️", category: "Queue Management" },
      { name: "nftQueueIsEmpty", title: "Queue Is Empty", inputs: [], icon: "❓", category: "Queue Management", formatOutput: (v) => (v ? "Yes" : "No") },
      { name: "checkQueue", title: "Check Queue", inputs: [{ name: "tokenId", type: "uint256", placeholder: "Token ID", required: true, label: "Token ID" }], icon: "🔍", category: "Queue Management", formatOutput: (v) => (v ? "Yes" : "No") },
      { name: "nftQueue", title: "NFT Queue At Index", inputs: [{ name: "", type: "uint256", placeholder: "Index", required: true, label: "Index" }], icon: "🔢", category: "Queue Management" },
      { name: "nftQueueDeleted", title: "NFT Queue Deleted", inputs: [{ name: "", type: "uint256", placeholder: "Index", required: true, label: "Index" }], icon: "🗑️", category: "Queue Management", formatOutput: (v) => (v ? "Yes" : "No") },
      { name: "nftQueueTokenIndex", title: "NFT Queue Token Index", inputs: [{ name: "", type: "uint256", placeholder: "Token ID", required: true, label: "Token ID" }], icon: "🔢", category: "Queue Management" },
      { name: "getNftQueue", title: "Get Full NFT Queue", inputs: [], icon: "📋", category: "Queue Management", formatOutput: (v) => (Array.isArray(v) ? v.join(", ") || "Empty" : "Empty") },
      { id: "getNftQueuePaginated", name: "getNftQueue", title: "Get NFT Queue (Paginated)", description: "Get NFT queue between start and end index", inputs: [{ name: "", type: "uint256", placeholder: "Start Index", required: true, label: "Start Index" }, { name: "", type: "uint256", placeholder: "End Index", required: true, label: "End Index" }], icon: "📄", category: "Queue Management", formatOutput: (v) => (Array.isArray(v) ? v.join(", ") || "Empty" : "Empty") },

      // User Profile
      { name: "getUserAllNfts", title: "Get User All NFTs", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "🎨", category: "User Profile", formatOutput: (v) => v.join(", ") || "None" },
      { name: "getUserOwnedNftLength", title: "User NFT Count", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "📏", category: "User Profile" },
      { name: "getUserOwnedNftAt", title: "User NFT At Index", inputs: [{ name: "account", type: "address", label: "User Address" }, { name: "index", type: "uint256", label: "Index" }], icon: "🔢", category: "User Profile" },
      { name: "getUserOwnedNftContains", title: "User Owns NFT", inputs: [{ name: "account", type: "address", label: "User Address" }, { name: "tokenId", type: "uint256", label: "Token ID" }], icon: "🔍", category: "User Profile", formatOutput: (v) => (v ? "Yes" : "No") },
      { name: "getUserHoldLength", title: "User Hold Length", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "📏", category: "User Profile" },
      { name: "getUserNextSale", title: "User Next Sale", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "⏭️", category: "User Profile" },
      { name: "getUserNftQueue", title: "User NFT Queue", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "📋", category: "User Profile", formatOutput: (v) => (Array.isArray(v) ? v.join(", ") || "Empty" : "Empty") },
      { name: "userProfiles", title: "User Profile Details", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "👥", category: "User Profile" },
      { name: "lastPurchaseAt", title: "Last Purchase At", inputs: [{ name: "account", type: "address", placeholder: "User Address", required: true, label: "User Address" }], icon: "⏰", category: "User Profile" },
      { name: "userDayPurchase", title: "User Day Purchase", inputs: [{ name: "", type: "address", placeholder: "User Address", required: true, label: "User Address" }, { name: "", type: "uint256", placeholder: "Day", required: true, label: "Day" }], icon: "📅", category: "User Profile" },
      { name: "userNftQueue", title: "User NFT Queue At Index", inputs: [{ name: "", type: "address", placeholder: "User Address", required: true, label: "User Address" }, { name: "", type: "uint256", placeholder: "Index", required: true, label: "Index" }], icon: "🔢", category: "User Profile" },

      // Trading Info
      { name: "getNftSaleDetails", title: "Get NFT Sale Details", inputs: [{ name: "tokenId", type: "uint256", placeholder: "Token ID", required: true, label: "Token ID" }], icon: "📊", category: "Trading" },
      { name: "nftOwner", title: "NFT Owner", inputs: [{ name: "tokenId", type: "uint256", placeholder: "Token ID", required: true, label: "Token ID" }], icon: "👤", category: "Trading" },
      { name: "nftSale", title: "NFT Sale Info", inputs: [{ name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }], icon: "🏪", category: "Trading" },
      { name: "nftTimeInfo", title: "NFT Time Info", inputs: [{ name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }], icon: "⏰", category: "Trading" },
      { name: "lastSaleTimeStamp", title: "Last Sale Timestamp", inputs: [{ name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }], icon: "⏰", category: "Trading" },
    ],

    // ── WRITE FUNCTIONS ─────────────────────────────────────────
    writeFunctions: [
      { name: "buyNft", title: "Buy NFT", inputs: [], requiresApproval: true, requiresBalanceCheck: true, icon: "🛒", category: "User Actions" },
      { name: "placeOrder", title: "Place Order", inputs: [{ name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }], requiresApproval: true, requiresBalanceCheck: true, icon: "📝", category: "User Actions" },
      { name: "claimNft", title: "Claim NFT", inputs: [], icon: "🎁", category: "User Actions" },
      { name: "burnNftId", title: "Burn NFT", inputs: [{ name: "nftTokenId", type: "uint256", placeholder: "Token ID", required: true, label: "Token ID" }], icon: "🔥", category: "User Actions" },
      { name: "burnNftFromOwner", title: "Burn NFT From Owner", inputs: [{ name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }], icon: "🔥", category: "User Actions" },
      { name: "burnNftReward", title: "Burn NFT Reward", inputs: [{ name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" }], icon: "🔥", category: "User Actions" },
      {
        name: "bulkNurnNftId",
        title: "Bulk Burn NFTs",
        inputs: [{ name: "tokenIds", type: "uint256[]", placeholder: "Token IDs (comma separated)", required: true, label: "Token IDs", isArray: true }],
        icon: "🔥",
        category: "User Actions",
      },
      { name: "enableAutoTrade", title: "Enable Auto Trade", inputs: [{ name: "status", type: "bool", placeholder: "true / false", required: true, label: "Status" }], icon: "🤖", category: "Auto Trade" },
      {
        name: "autoTradeBatch",
        title: "Auto Trade Batch",
        inputs: [
          { name: "tokenIds", type: "uint256[]", placeholder: "Token IDs (comma separated)", required: true, label: "Token IDs", isArray: true },
          { name: "bots", type: "address[]", placeholder: "Bot Addresses (comma separated)", required: true, label: "Bot Addresses", isArray: true },
        ],
        icon: "🤖",
        category: "Auto Trade",
      },
      { name: "checkBotStatusExternal", title: "Check Bot Status", inputs: [{ name: "account", type: "address", placeholder: "Bot Address", required: true, label: "Bot Address" }], icon: "🤖", category: "Auto Trade" },
      {
        name: "onERC721Received",
        title: "On ERC721 Received",
        inputs: [
          { name: "operator", type: "address", required: true, label: "Operator" },
          { name: "from", type: "address", required: true, label: "From" },
          { name: "tokenId", type: "uint256", required: true, label: "Token ID" },
          { name: "data", type: "bytes", required: false, label: "Data (0x for empty)" },
        ],
        icon: "🔄",
        category: "Advanced",
      },
    ],

    // ── ADMIN FUNCTIONS ─────────────────────────────────────────
    adminFunctions: [
      { name: "pause", title: "Pause Contract", inputs: [], icon: "⏸️", category: "Admin Control" },
      { name: "unpause", title: "Unpause Contract", inputs: [], icon: "▶️", category: "Admin Control" },
      { name: "renounceOwnership", title: "Renounce Ownership", inputs: [], icon: "🚫", category: "Admin Control" },
      { name: "transferOwnership", title: "Transfer Ownership", inputs: [{ name: "newOwner", type: "address", placeholder: "New Owner Address", required: true, label: "New Owner" }], icon: "🔄", category: "Admin Control" },
      {
        name: "initialize",
        title: "Initialize",
        inputs: [
          { name: "initialOwner", type: "address", required: true, label: "Initial Owner" },
          { name: "usdtTokenAddress", type: "address", required: true, label: "USDT Token Address" },
        ],
        icon: "🔧",
        category: "Admin Control",
      },
      { name: "newSetUp", title: "New Setup", inputs: [], icon: "⚙️", category: "Admin Control" },
      { name: "bulkImportBots", title: "Bulk Import Bots", inputs: [{ name: "accounts", type: "address[]", placeholder: "Addresses (comma separated)", required: true, label: "Addresses", isArray: true }], icon: "🤖", category: "Admin Control" },
      { name: "compactQueue", title: "Compact Queue", inputs: [{ name: "maxSteps", type: "uint256", placeholder: "Max Steps", required: true, label: "Max Steps" }], icon: "🧹", category: "Admin Control" },

      // Recovery
      {
        name: "recoverErc20",
        title: "Recover ERC20",
        inputs: [
          { name: "erc20Token", type: "address", placeholder: "Token Address", required: true, label: "Token Address" },
          { name: "amount", type: "uint256", placeholder: "Amount (wei)", required: true, label: "Amount" },
        ],
        icon: "♻️",
        category: "Admin Recovery",
      },
      { name: "recoverEth", title: "Recover ETH/BNB", inputs: [{ name: "amount", type: "uint256", placeholder: "Amount (wei)", required: true, label: "Amount" }], icon: "♻️", category: "Admin Recovery" },
      {
        name: "recoverNft",
        title: "Recover NFT",
        inputs: [
          { name: "receiver", type: "address", placeholder: "Receiver Address", required: true, label: "Receiver" },
          { name: "nftId", type: "uint256", placeholder: "NFT ID", required: true, label: "NFT ID" },
        ],
        icon: "♻️",
        category: "Admin Recovery",
      },

      // NFT Transfer
      {
        name: "transferNftToOwner",
        title: "Transfer NFT To Owner",
        inputs: [
          { name: "start", type: "uint256", placeholder: "Start Token ID", required: true, label: "Start" },
          { name: "end", type: "uint256", placeholder: "End Token ID", required: true, label: "End" },
        ],
        icon: "📤",
        category: "Admin NFT Transfer",
      },
      {
        name: "transferNftToUser",
        title: "Transfer NFT To User",
        inputs: [
          { name: "accounts", type: "address[]", placeholder: "Addresses (comma separated)", required: true, label: "Addresses", isArray: true },
          { name: "tokenIds", type: "uint256[]", placeholder: "Token IDs (comma separated)", required: true, label: "Token IDs", isArray: true },
        ],
        icon: "📤",
        category: "Admin NFT Transfer",
      },

      // Settings
      {
        name: "setBurnAndMint",
        title: "Set Burn & Mint",
        inputs: [
          { name: "newMintNftCount", type: "uint256", required: true, label: "New Mint NFT Count" },
          { name: "newMaxSalePrice", type: "uint256", required: true, label: "New Max Sale Price" },
        ],
        icon: "🔥",
        category: "Admin Settings",
      },
      { name: "setClaimStatus", title: "Set Claim Status", inputs: [{ name: "status", type: "bool", placeholder: "true/false", required: true, label: "Status" }], icon: "✅", category: "Admin Settings" },
      { name: "setGlobalPeriod", title: "Set Global Period", inputs: [{ name: "newGlobalPeriod", type: "uint256", required: true, label: "New Global Period" }], icon: "⏱️", category: "Admin Settings" },
      {
        name: "setLastPurchaseAt",
        title: "Set Last Purchase At",
        inputs: [
          { name: "account", type: "address", required: true, label: "User Address" },
          { name: "newPeriod", type: "uint256", required: true, label: "New Period (timestamp)" },
        ],
        icon: "⏰",
        category: "Admin Settings",
      },
      { name: "setMaxNftSupply", title: "Set Max NFT Supply", inputs: [{ name: "newSupply", type: "uint256", required: true, label: "New Supply" }], icon: "📈", category: "Admin Settings" },
      { name: "setMaxSalePrice", title: "Set Max Sale Price", inputs: [{ name: "newMaxSalePrice", type: "uint256", required: true, label: "New Max Sale Price" }], icon: "💲", category: "Admin Settings" },
      { name: "setNftBurnBasePrice", title: "Set NFT Burn Base Price", inputs: [{ name: "newNftBurnBasePrice", type: "uint256", required: true, label: "New Burn Base Price" }], icon: "🔥", category: "Admin Settings" },
      { name: "setNftContract", title: "Set NFT Contract", inputs: [{ name: "newNftAddress", type: "address", required: true, label: "New NFT Address" }], icon: "🎨", category: "Admin Settings" },
      { name: "setNftMatrixContract", title: "Set NFT Matrix Contract", inputs: [{ name: "newNftMatrix", type: "address", required: true, label: "New NFT Matrix" }], icon: "🤖", category: "Admin Settings" },
      { name: "setNftPrice", title: "Set NFT Base Price", inputs: [{ name: "newPrice", type: "uint256", required: true, label: "New Price" }], icon: "💵", category: "Admin Settings" },
      { name: "setNftRewardContract", title: "Set NFT Reward Contract", inputs: [{ name: "newNftReward", type: "address", required: true, label: "New NFT Reward" }], icon: "🏆", category: "Admin Settings" },
      {
        name: "setPlatformRewardReceiver",
        title: "Set Platform & Burn Receiver",
        inputs: [
          { name: "newPlatformRewardReceiver", type: "address", required: true, label: "Platform Reward Receiver" },
          { name: "newBurnFeeReceiver", type: "address", required: true, label: "Burn Fee Receiver" },
        ],
        icon: "📤",
        category: "Admin Settings",
      },
      { name: "setQueueRequire", title: "Set Queue Require", inputs: [{ name: "newQueueRequire", type: "uint256", required: true, label: "New Queue Require" }], icon: "📋", category: "Admin Settings" },
      {
        name: "setSaleIncomePercent",
        title: "Set Sale Income Percent",
        inputs: [
          { name: "newNftOwnerIncomePercent", type: "uint256", required: true, label: "NFT Owner %" },
          { name: "newLevelIncomePercent", type: "uint256", required: true, label: "Level Income %" },
          { name: "newBuringIncomePercent", type: "uint256", required: true, label: "Burning Income %" },
          { name: "newPlatformIncomePercent", type: "uint256", required: true, label: "Platform Income %" },
        ],
        icon: "📊",
        category: "Admin Settings",
      },
      { name: "setSellPercent", title: "Set Sell Percent", inputs: [{ name: "newSellPercent", type: "uint256", required: true, label: "New Sell Percent" }], icon: "📉", category: "Admin Settings" },
      { name: "setUsdtToken", title: "Set USDT Token", inputs: [{ name: "newUsdtToken", type: "address", required: true, label: "New USDT Token" }], icon: "💰", category: "Admin Settings" },
      {
        name: "setUserBlockRewardCondition",
        title: "Set User Block Reward",
        inputs: [
          { name: "userAccount", type: "address", required: true, label: "User Address" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Block Status" },
        ],
        icon: "🚫",
        category: "Admin User Management",
      },
      {
        name: "setUserExcludeRewardCondition",
        title: "Set User Exclude Reward",
        inputs: [
          { name: "userAccount", type: "address", required: true, label: "User Address" },
          { name: "status", type: "bool", placeholder: "true/false", required: true, label: "Exclude Status" },
        ],
        icon: "🔕",
        category: "Admin User Management",
      },

      // Auto Trade Settings
      { name: "setAutoTradeFee", title: "Set Auto Trade Fee", inputs: [{ name: "newFee", type: "uint256", required: true, label: "New Fee" }], icon: "💸", category: "Auto Trade Settings" },
      { name: "setAutoTradeFeeEnable", title: "Set Auto Trade Fee Enable", inputs: [{ name: "status", type: "bool", required: true, label: "Status" }], icon: "💸", category: "Auto Trade Settings" },
      { name: "setAutoTradeFeeReceiver", title: "Set Auto Trade Fee Receiver", inputs: [{ name: "newWallet", type: "address", required: true, label: "New Wallet" }], icon: "📥", category: "Auto Trade Settings" },
    ],
  };

  // ─────────────────────────────────────────────────────────────
  //  WALLET CONNECTION
  // ─────────────────────────────────────────────────────────────
  const connectWallet = async () => {
    setLoadingStates((prev) => ({ ...prev, connect: true }));
    try {
      const acc = await loadWeb3();
      if (acc === "No Wallet") {
        toast.error("No Wallet Detected");
        setBtnTxt("Connect Wallet");
      } else if (acc === "Wrong Network") {
        toast.error("Please switch to the correct Network");
        setBtnTxt("Wrong Network");
      } else {
        setAccount(acc);
        setBtnTxt(acc.substring(0, 6) + "..." + acc.substring(acc.length - 4));
        toast.success("Wallet Connected!");
        loadMarketplaceData();
      }
    } catch (error) {
      console.error("Connection error:", error);
      toast.error("Connection failed");
      setBtnTxt("Connect Wallet");
    } finally {
      setLoadingStates((prev) => ({ ...prev, connect: false }));
    }
  };

  const getContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(pynexnftMarketplaceAbi, pynexnftMarketplace);
  };

  const getUsdtContract = () => {
    const web3 = getWeb3Instance();
    return new web3.eth.Contract(USDTAbi, USDT);
  };

  // ─────────────────────────────────────────────────────────────
  //  OVERVIEW DATA LOADER
  // ─────────────────────────────────────────────────────────────
  const loadMarketplaceData = useCallback(async () => {
    if (!account) return;
    try {
      const web3 = getWeb3Instance();
      const contract = getContract();
      const usdtContract = getUsdtContract();

      const [
        totalNftsMinted,
        maxNftSupply,
        nftBasePrice,
        nftQueueLength,
        isClaimEnable,
        contractUsdtBalance,
        userUsdtBalance,
      ] = await Promise.all([
        contract.methods.totalNftsMinted().call().catch(() => "0"),
        contract.methods.maxNftSupply().call().catch(() => "0"),
        contract.methods.nftBasePrice().call().catch(() => "0"),
        contract.methods.nftQueueLength().call().catch(() => "0"),
        contract.methods.isClaimEnable().call().catch(() => false),
        usdtContract.methods.balanceOf(pynexnftMarketplace).call().catch(() => "0"),
        usdtContract.methods.balanceOf(account).call().catch(() => "0"),
      ]);

      setMarketplaceData({
        totalNftsMinted: totalNftsMinted.toString(),
        maxNftSupply: maxNftSupply.toString(),
        nftBasePrice: web3.utils.fromWei(nftBasePrice.toString(), "ether"),
        queueLength: nftQueueLength.toString(),
        isClaimEnabled: isClaimEnable,
        usdtBalance: web3.utils.fromWei(contractUsdtBalance.toString(), "ether"),
        userUsdtBalance: web3.utils.fromWei(userUsdtBalance.toString(), "ether"),
      });
    } catch (error) {
      console.error("Error loading marketplace data:", error);
      toast.error("Failed to load marketplace data");
    }
  }, [account]);

  useEffect(() => {
    if (account) loadMarketplaceData();
  }, [account, loadMarketplaceData]);

  // ─────────────────────────────────────────────────────────────
  //  INPUT HANDLING
  // ─────────────────────────────────────────────────────────────
  const handleInputChange = (functionName, inputName, value) => {
    setFunctionInputs((prev) => ({
      ...prev,
      [functionName]: { ...prev[functionName], [inputName]: value },
    }));
  };

  // ─────────────────────────────────────────────────────────────
  //  INPUT VALIDATION
  // ─────────────────────────────────────────────────────────────
  const validateInput = (type, value) => {
    if (type === "address" && !getWeb3Instance().utils.isAddress(value)) {
      throw new Error(`Invalid address: ${value}`);
    }
    if (type.startsWith("uint") && (isNaN(value) || Number(value) < 0)) {
      throw new Error(`Invalid uint value: ${value}`);
    }
    if (type === "bool" && !["true", "false"].includes(value.toString().toLowerCase())) {
      throw new Error(`Invalid bool value: ${value}`);
    }
    if (type === "bytes" && !value.startsWith("0x")) {
      throw new Error("Bytes must start with 0x");
    }
    return true;
  };

  // Build args array from function definition + current inputs
  const buildArgs = (functionData, inputs) => {
    return functionData.inputs.map((input) => {
      // unnamed inputs use index as key
      const key = input.name !== undefined && input.name !== "" ? input.name : functionData.inputs.indexOf(input).toString();
      let value = inputs[key] !== undefined ? inputs[key] : "";

      if (input.required && String(value).trim() === "") {
        throw new Error(`${input.label || input.name || "Input"} is required`);
      }

      // Arrays (comma-separated)
      if (input.isArray && typeof value === "string") {
        value = value.split(",").map((v) => v.trim()).filter(Boolean);
        value.forEach((v) => validateInput(input.type.replace("[]", ""), v));
        return value;
      }

      // Empty optional input — pass default
      if (!input.required && String(value).trim() === "") {
        if (input.type === "bytes") return "0x";
        if (input.type.startsWith("uint")) return "0";
        if (input.type === "address") return "0x0000000000000000000000000000000000000000";
        return value;
      }

      validateInput(input.type, value);

      if (input.type === "bool") return String(value).toLowerCase() === "true";
      return value;
    });
  };

  // ─────────────────────────────────────────────────────────────
  //  EXECUTE READ FUNCTION
  // ─────────────────────────────────────────────────────────────
  const executeReadFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.id || functionData.name;
    setLoadingStates((prev) => ({ ...prev, [funcKey]: true }));

    try {
      const contract = getContract();
      const inputs = functionInputs[funcKey] || {};
      const args = buildArgs(functionData, inputs);

      let result = await contract.methods[functionData.name](...args).call({ from: account });

      // Convert BigInt safely
      const convertBigInt = (val) => {
        if (typeof val === "bigint") return val.toString();
        if (Array.isArray(val)) return val.map(convertBigInt);
        if (typeof val === "object" && val !== null) {
          const obj = {};
          Object.keys(val).forEach((k) => { obj[k] = convertBigInt(val[k]); });
          return obj;
        }
        return val;
      };
      result = convertBigInt(result);

      const formattedResult = functionData.formatOutput
        ? functionData.formatOutput(result)
        : JSON.stringify(result, (_, v) => (typeof v === "bigint" ? v.toString() : v), 2);

      setFunctionOutputs((prev) => ({ ...prev, [funcKey]: formattedResult }));
      toast.success("Read successful!");
    } catch (error) {
      console.error("Read error:", error);
      toast.error(error.message || "Read operation failed");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [funcKey]: false }));
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  EXECUTE WRITE FUNCTION  ← All critical bugs fixed here
  // ─────────────────────────────────────────────────────────────
  const executeWriteFunction = async (functionData) => {
    if (!account) {
      toast.error("Please connect your wallet first");
      return;
    }

    const funcKey = functionData.id || functionData.name;
    setLoadingStates((prev) => ({ ...prev, [funcKey]: true }));

    try {
      const contract = getContract();
      const usdtContract = getUsdtContract();
      const inputs = functionInputs[funcKey] || {};

      // Build and validate args
      const args = buildArgs(functionData, inputs);

      // ── Paused check ──────────────────────────────────────────
      const isPaused = await contract.methods.paused().call();
      if (isPaused && functionData.name !== "unpause") {
        throw new Error("Contract is currently paused");
      }

      // ── Owner check for Admin functions ───────────────────────
      if (functionData.category && functionData.category.startsWith("Admin")) {
        const contractOwner = await contract.methods.owner().call();
        if (contractOwner.toLowerCase() !== account.toLowerCase()) {
          throw new Error("Only the contract owner can call this function");
        }
      }

      // ── USDT Balance + Approval for buy/order functions ───────
      if (functionData.requiresBalanceCheck || functionData.requiresApproval) {
        let requiredAmount = "0";

        if (functionData.name === "buyNft") {
          requiredAmount = await contract.methods.nftBasePrice().call();
        } else if (functionData.name === "placeOrder") {
          // ✅ FIX: nftSalePrice() does NOT exist → use nftSale() which returns struct
          const nftId = args[0];
          const saleInfo = await contract.methods.nftSale(nftId).call();
          // saleInfo: { nftBasePrice, nftOwner, isSale }
          requiredAmount = saleInfo.nftBasePrice || saleInfo[0] || "0";
        }

        // Balance check
        const userBalance = await usdtContract.methods.balanceOf(account).call();
        if (Number(userBalance) < Number(requiredAmount)) {
          throw new Error(`Insufficient USDT balance. Required: ${requiredAmount}`);
        }

        // Approval check
        if (functionData.requiresApproval) {
          const allowance = await usdtContract.methods.allowance(account, pynexnftMarketplace).call();
          if (Number(allowance) < Number(requiredAmount)) {
            toast.info("Approving USDT — please confirm in MetaMask...");
            const approveGas = await usdtContract.methods
              .approve(pynexnftMarketplace, requiredAmount)
              .estimateGas({ from: account });
            await usdtContract.methods
              .approve(pynexnftMarketplace, requiredAmount)
              .send({ from: account, gas: Math.floor(Number(approveGas) * 1.5) });
            toast.success("USDT Approved!");
          }
        }
      }

      // ── Simulation (soft — don't block on ambiguous RPC errors) ──
      // ✅ FIX: Only throw on real revert reasons, not on JSON-RPC infra errors
      try {
        await contract.methods[functionData.name](...args).call({ from: account });
      } catch (simError) {
        const simMsg = simError?.message || simError?.data?.message || "";
        // "Internal JSON-RPC error" is an infra issue, not a contract revert — skip it
        if (
          !simMsg.toLowerCase().includes("internal json-rpc") &&
          !simMsg.toLowerCase().includes("unknown error")
        ) {
          throw new Error(`Simulation failed: ${simMsg}`);
        }
        // Otherwise: warn but continue — estimateGas will also catch hard reverts
        console.warn("Simulation soft-warning (proceeding):", simMsg);
      }

      // ── Gas estimation ────────────────────────────────────────
      let gasEstimate;
      try {
        gasEstimate = await contract.methods[functionData.name](...args).estimateGas({ from: account });
      } catch (estError) {
        const estMsg = estError?.message || "";
        throw new Error(`Gas estimation failed — transaction will revert. Reason: ${estMsg}`);
      }

      const gasWithBuffer = Math.floor(Number(gasEstimate) * 1.5);

      // ── Send transaction ──────────────────────────────────────
      toast.info("Please confirm the transaction in MetaMask...");
      const tx = await contract.methods[functionData.name](...args).send({
        from: account,
        gas: gasWithBuffer,
      });

      toast.success(`✅ Transaction successful! Hash: ${tx.transactionHash}`);
      loadMarketplaceData();
    } catch (error) {
      console.error("Write error:", error);
      toast.error(error.message || "Write operation failed");
    } finally {
      setLoadingStates((prev) => ({ ...prev, [funcKey]: false }));
    }
  };

  // ─────────────────────────────────────────────────────────────
  //  HELPERS
  // ─────────────────────────────────────────────────────────────
  const groupFunctionsByCategory = (functions) =>
    functions.reduce((acc, func) => {
      const cat = func.category || "Other";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(func);
      return acc;
    }, {});

  // ─────────────────────────────────────────────────────────────
  //  RENDER FUNCTION CARD
  // ─────────────────────────────────────────────────────────────
  const renderFunctionCard = (func, isWrite = false) => {
    const funcKey = func.id || func.name;
    // For unnamed inputs, we use index as key (matching buildArgs logic)
    const inputsWithKeys = func.inputs.map((inp, idx) => ({
      ...inp,
      _key: inp.name !== undefined && inp.name !== "" ? inp.name : idx.toString(),
    }));
    const inputs = functionInputs[funcKey] || {};
    const output = functionOutputs[funcKey];
    const isLoading = loadingStates[funcKey] || false;

    return (
      <div key={funcKey} className="col-lg-4 col-md-6 mb-4">
        <div
          className="card h-100 shadow-lg border-0"
          style={{
            borderRadius: "20px",
            background: "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
            backdropFilter: "blur(15px)",
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <div className="card-body p-4 d-flex flex-column">
            <div className="d-flex align-items-center mb-3">
              <span className="fs-3 me-3">{func.icon}</span>
              <div>
                <h5 className="mb-1 text-white">{func.title}</h5>
                {func.description && <small className="text-white-50">{func.description}</small>}
              </div>
            </div>

            {inputsWithKeys.map((input) => (
              <div key={input._key} className="mb-3">
                <label className="form-label text-white-50 small mb-1">
                  {input.label || input.name || input._key}
                  {input.required && <span className="text-danger">*</span>}
                </label>
                <input
                  type="text"
                  className="form-control bg-dark border-0 text-white white-placeholder"
                  placeholder={input.placeholder || `Enter ${input.type}`}
                  value={inputs[input._key] || ""}
                  onChange={(e) => handleInputChange(funcKey, input._key, e.target.value)}
                  style={{ borderRadius: "12px", padding: "12px", fontSize: "0.9rem" }}
                />
                {input.isArray && (
                  <small className="text-white-50 d-block mt-1">Separate multiple values with commas</small>
                )}
              </div>
            ))}

            <button
              onClick={() => (isWrite ? executeWriteFunction(func) : executeReadFunction(func))}
              disabled={isLoading}
              className="btn w-100 mt-auto"
              style={{
                background: isWrite
                  ? "linear-gradient(135deg, #ff6b6b, #4ecdc4)"
                  : "linear-gradient(135deg, #00ff88, #00ccff)",
                border: "none",
                color: "#fff",
                borderRadius: "12px",
                padding: "12px",
                fontWeight: "600",
              }}
            >
              {isLoading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Processing...
                </span>
              ) : isWrite ? (
                "Execute Write"
              ) : (
                "Read"
              )}
            </button>

            {output && (
              <div
                className="mt-3 p-3 rounded-3"
                style={{
                  background: "rgba(0,255,136,0.1)",
                  border: "1px solid rgba(0,255,136,0.3)",
                  whiteSpace: "pre-wrap",
                  overflow: "auto",
                  maxHeight: "200px",
                }}
              >
                <small className="text-white-50 d-block mb-2">Output:</small>
                <code className="text-white">{output}</code>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div
      className="container-fluid min-vh-100 d-flex align-items-center justify-content-center p-4"
      style={{ background: "linear-gradient(135deg, #000428, #004e92)" }}
    >
      <div className="container text-center">
        <h1
          className="mb-5 display-4 fw-bold"
          style={{
            background: "linear-gradient(135deg, #00ff88, #00ccff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Pynex NFT Marketplace Dashboard
        </h1>

        {/* Connect Wallet */}
        <div className="mb-5">
          <button
            onClick={connectWallet}
            disabled={loadingStates.connect}
            className="btn px-4 px-md-5 py-3 rounded-pill shadow-lg position-relative overflow-hidden"
            style={{
              background: account
                ? "linear-gradient(135deg, #00ff88, #00ccff)"
                : "linear-gradient(135deg, #ff6b6b, #4ecdc4)",
              border: "none",
              color: account ? "#000" : "#fff",
              fontSize: "clamp(0.9rem, 3vw, 1.1rem)",
              fontWeight: "700",
              transition: "all 0.3s ease",
              minWidth: "160px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
              opacity: loadingStates.connect ? 0.7 : 1,
            }}
          >
            {loadingStates.connect ? (
              <div className="d-flex align-items-center justify-content-center">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Connecting...
              </div>
            ) : (
              <span className="position-relative">{btnTxt}</span>
            )}
          </button>
        </div>

        {account && (
          <div
            className="mt-4 p-3 rounded-4"
            style={{ background: "rgba(0,255,136,0.1)", border: "1px solid rgba(0,255,136,0.3)" }}
          >
            <div className="d-flex justify-content-center align-items-center gap-2">
              <span className="text-white-50 small">
                🔗 {account.substring(0, 6)}...{account.substring(account.length - 4)}
              </span>
              <span className="badge bg-success rounded-pill">Connected</span>
            </div>
          </div>
        )}

        {account && (
          <div className="row">
            <div className="col-12">
              <div
                className="card shadow-lg border-0 mb-4"
                style={{
                  borderRadius: "24px",
                  background: "rgba(255,255,255,0.05)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                {/* Tab Navigation */}
                <div
                  className="card-header border-0 p-4"
                  style={{ borderRadius: "24px 24px 0 0", background: "transparent" }}
                >
                  <ul className="nav nav-pills justify-content-center flex-wrap gap-2" role="tablist">
                    {[
                      { key: "overview", label: "Overview", icon: "🏪", color: "#00ff88" },
                      { key: "read", label: "Read Functions", icon: "👁️", color: "#45b7d1" },
                      { key: "write", label: "Write Functions", icon: "📝", color: "#4ecdc4" },
                      { key: "admin", label: "Admin Functions", icon: "⚙️", color: "#ff6b6b" },
                    ].map((tab) => (
                      <li key={tab.key} className="nav-item">
                        <button
                          className={`nav-link border-0 px-4 py-3 fw-bold position-relative overflow-hidden ${
                            activeTab === tab.key ? "text-dark" : "text-white-50"
                          }`}
                          onClick={() => setActiveTab(tab.key)}
                          style={{
                            borderRadius: "16px",
                            background:
                              activeTab === tab.key
                                ? `linear-gradient(135deg, ${tab.color}, ${tab.color}80)`
                                : "rgba(255,255,255,0.05)",
                            border:
                              activeTab === tab.key
                                ? `1px solid ${tab.color}`
                                : "1px solid rgba(255,255,255,0.1)",
                            transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
                            transform: activeTab === tab.key ? "translateY(-2px)" : "translateY(0)",
                            boxShadow: activeTab === tab.key ? `0 8px 25px ${tab.color}40` : "none",
                          }}
                        >
                          <span className="me-2 fs-5">{tab.icon}</span>
                          <span className="d-none d-md-inline">{tab.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div
                  className="card-body p-4"
                  style={{ background: "rgba(0,0,0,0.2)", borderRadius: "0 0 24px 24px" }}
                >
                  {/* ── OVERVIEW TAB ── */}
                  {activeTab === "overview" && (
                    <div className="row g-4">
                      {[
                        { icon: "🎨", value: marketplaceData.totalNftsMinted || "0", label: "Total Minted", color: "#00ff88", func: "totalNftsMinted" },
                        { icon: "📈", value: marketplaceData.maxNftSupply || "0", label: "Max Supply", color: "#45b7d1", func: "maxNftSupply" },
                        { icon: "💵", value: `${marketplaceData.nftBasePrice || "0"} USDT`, label: "Base Price", color: "#6be0d7", func: "nftBasePrice" },
                        { icon: "📋", value: marketplaceData.queueLength || "0", label: "Queue Length", color: "#ffaa00", func: "nftQueueLength" },
                        {
                          icon: marketplaceData.isClaimEnabled ? "✅" : "❌",
                          value: marketplaceData.isClaimEnabled ? "ENABLED" : "DISABLED",
                          label: "Claim Status",
                          color: marketplaceData.isClaimEnabled ? "#00ff88" : "#ff6b6b",
                          func: "isClaimEnable",
                        },
                        { icon: "💰", value: `${marketplaceData.usdtBalance || "0"} USDT`, label: "Contract Balance", color: "#ffaa00", func: null },
                        { icon: "💼", value: `${marketplaceData.userUsdtBalance || "0"} USDT`, label: "Your Balance", color: "#00ff88", func: null },
                      ].map((card, idx) => (
                        <div key={idx} className="col-lg-3 col-md-6">
                          <div
                            className="card h-100 border-0"
                            style={{
                              borderRadius: "20px",
                              background: `linear-gradient(135deg, ${card.color}26, ${card.color}1a)`,
                              border: `1px solid ${card.color}4d`,
                              backdropFilter: "blur(10px)",
                            }}
                          >
                            <div className="card-body p-4 text-center">
                              <div className="fs-1 mb-3">{card.icon}</div>
                              <h4 className="text-white mb-2">{card.value}</h4>
                              <p className="text-white-50 mb-0">{card.label}</p>
                              <button
                                className="btn btn-sm mt-3 w-100"
                                onClick={() => {
                                  if (card.func) {
                                    const fn = marketplaceFunctions.readFunctions.find((f) => f.name === card.func);
                                    if (fn) executeReadFunction(fn);
                                  } else {
                                    loadMarketplaceData();
                                  }
                                }}
                                style={{
                                  background: `${card.color}33`,
                                  border: `1px solid ${card.color}`,
                                  color: card.color,
                                }}
                              >
                                🔄 Refresh
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── READ FUNCTIONS TAB ── */}
                  {activeTab === "read" && (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0 text-info">👁️ Read Functions</h4>
                        <small className="text-white-50">{marketplaceFunctions.readFunctions.length} functions</small>
                      </div>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.readFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50 d-flex align-items-center">
                            <span className="me-2">{category}</span>
                            <span className="badge bg-secondary rounded-pill">{funcs.length}</span>
                          </h5>
                          <div className="row">{funcs.map((func) => renderFunctionCard(func, false))}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── WRITE FUNCTIONS TAB ── */}
                  {activeTab === "write" && (
                    <div>
                      <h4 className="mb-4" style={{ color: "#4ecdc4" }}>📝 Write Functions</h4>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.writeFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">{funcs.map((func) => renderFunctionCard(func, true))}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── ADMIN FUNCTIONS TAB ── */}
                  {activeTab === "admin" && (
                    <div>
                      <h4 className="mb-4" style={{ color: "#ff6b6b" }}>⚙️ Admin Functions</h4>
                      <div
                        className="alert mb-4"
                        style={{
                          background: "rgba(255,107,107,0.1)",
                          border: "1px solid rgba(255,107,107,0.3)",
                          borderRadius: "12px",
                          color: "#ff6b6b",
                        }}
                      >
                        ⚠️ <strong>Warning:</strong> These functions are restricted to the contract owner/admin. Unauthorized calls will be reverted.
                      </div>
                      {Object.entries(groupFunctionsByCategory(marketplaceFunctions.adminFunctions)).map(([category, funcs]) => (
                        <div key={category} className="mb-5">
                          <h5 className="mb-3 text-white-50">{category}</h5>
                          <div className="row">{funcs.map((func) => renderFunctionCard(func, true))}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}